// Dub / lofi reggae (docs/AUDIO_DESIGN.md §12): a one-drop riddim through a dusty tape echo (King Tubby and Augustus Pablo
// via a cassette). No kick on 1: the kick and a cross-stick land together on beat 3, the hats swing their 8ths, and upbeat
// adds rockers and steppers (four on the floor). The skank chops the off-beats on minor triads and m7s in two-chord vamps: a
// muted guitar chop and / or a drawbar organ whose left hand bubbles on the "and"s. A deep, round sub plays a melodic riddim
// with rests and drops out for a bar now and then; a melodica plays simple tunes. One tape echo and one spring per track:
// the cross-stick, the snare, the skank and the melodica send into them, and now and then one backbeat or one chop is
// thrown into a long echo tail.
import { rng } from '../../engine/core.js';
import { QUALITIES, trackProgs } from '../harmony.js';
import { AND, createDesk, createSkank, createDubBass, createDubKit, createMelodica } from './dub-voices.js';

const mod12 = n => ((n % 12) + 12) % 12;
const ENERGY = { chill: 0, balanced: 1, upbeat: 2 };
const pickW = (r, w) => {
  let u = r() * Object.values(w).reduce((a, b) => a + b, 0);
  for (const [k, v] of Object.entries(w)) if ((u -= v) < 0) return k;
  return Object.keys(w)[0];
};
/** the swung "and" of the beat that starts on step b (AND of a step late; the engine adds its swing of a fractional step) */
const and = b => +(b + AND).toFixed(2);
const MODES = {                                                   // the bass line's scale, from the chords (semitones)
  aeolian: [0, 2, 3, 5, 7, 8, 10], dorian: [0, 2, 3, 5, 7, 9, 10], ionian: [0, 2, 4, 5, 7, 9, 11], mixolydian: [0, 2, 4, 5, 7, 9, 10],
};

/* ---------------- skank voicings: a low note (the organ's left hand) + a close triad on top ---------------- */
// Tones: the chord's root, 3rd (or sus), 5th and 7th / 6th, no 9ths (the final tonic is i7 in minor, a triad in major). The
// upper three: every inversion in close position of the triad, and of 3-5-7 and 1-3-7 on a 7th chord (the skank's m7), the
// lowest MIDI 54–66, the top 60–75; the low note is the root at MIDI 43–55 (nearest the last), an octave lower (40–43) when
// it would sit less than a minor 3rd under the upper three. Held chords and the intro play all four; the chops pick the top
// three, the bubble the low note.
const tonesOf = (c, plan) => {
  if (c.final) return plan.key.mode === 'minor' ? [0, 3, 7, 10] : [0, 4, 7];
  const set = [...new Set((QUALITIES[c.q] || [0, 4, 7]).map(i => i % 12))];
  const third = set.find(i => i === 3 || i === 4) ?? set.find(i => i === 5 || i === 2), fifth = set.find(i => i === 7 || i === 6);
  const sev = set.find(i => i === 10 || i === 11) ?? set.find(i => i === 9);
  return [0, third, fifth, sev].filter(x => x != null);
};
function uppers(tones) {
  const sets = [tones.slice(0, 3)];
  if (tones.length > 3) sets.push([tones[1], tones[2], tones[3]], [tones[0], tones[1], tones[3]]);
  return sets;
}
const CANDS = new Map();                                          // `${root}:${tones}` → [{ u, seventh }]
function candidates(root, tones) {
  const key = `${root}:${tones}`;
  if (CANDS.has(key)) return CANDS.get(key);
  const out = [];
  for (const set of uppers(tones)) {
    const pcs = set.map(i => mod12(root + i)), seventh = tones.length > 3 && set.includes(tones[3]);
    for (let k = 0; k < 3; k++) for (let u0 = 54 + mod12(pcs[k] - 54); u0 <= 66; u0 += 12) {
      const u = [u0];
      for (let j = 1; j < 3; j++) { const pc = pcs[(k + j) % 3]; u.push(u[j - 1] + 1 + mod12(pc - u[j - 1] - 1)); }
      if (u[2] >= 60 && u[2] <= 75 && u[2] - u[0] <= 10) out.push({ u, seventh });
    }
  }
  CANDS.set(key, out);
  return out;
}
const mean = a => a.reduce((s, v) => s + v, 0) / a.length;
/** Cost: Σ|Δupper| + 2·max(0, |Δtop| − 3) + .35·|mean − 64| − .6 with the 7th; the first chord of a track
 *  .6·|mean − (64 + register)| + .3·|top − (69 + register)| instead (plan.register: tracks don't all open on the same
 *  notes). The same chord keeps its voicing (a riddim's shape barely moves). */
export function skankVoicing(sp, prev, c, plan) {
  const root = mod12(plan.key.tonic + c.root), tones = tonesOf(c, plan), reg = plan.register || 0;
  if (prev && prev.length === 4 && mod12(prev[0]) === root) {
    const want = new Set(tones.map(i => mod12(root + i))), have = new Set(prev.slice(1).map(mod12));
    const sev = tones.length > 3 ? mod12(root + tones[3]) : null;
    if ([...have].every(pc => want.has(pc)) && (sev == null || have.has(sev))) return prev;
  }
  let best = null, bc = Infinity;
  for (const { u, seventh } of candidates(root, tones)) {
    const m = mean(u);
    let k = seventh ? -0.6 : 0;
    if (prev && prev.length === 4) {
      const pu = prev.slice(1);
      k += Math.abs(u[0] - pu[0]) + Math.abs(u[1] - pu[1]) + Math.abs(u[2] - pu[2]) + 2 * Math.max(0, Math.abs(u[2] - pu[2]) - 3) + 0.35 * Math.abs(m - 64);
    } else k += 0.6 * Math.abs(m - 64 - reg) + 0.3 * Math.abs(u[2] - 69 - reg);
    if (k < bc) { bc = k; best = u; }
  }
  if (!best) best = tones.slice(0, 3).map(i => 57 + mod12(root + i - 57)).sort((a, b) => a - b);
  // the low note: the root at MIDI 43–55 nearest the last low note (else 49), at least 3 under the upper three
  const want = prev ? prev[0] : 49;
  let low = 43 + mod12(root - 43);
  if (low + 12 <= 55 && Math.abs(low + 12 - want) < Math.abs(low - want)) low += 12;
  while (low > best[0] - 3) low -= 12;
  return [low, ...best];
}

/* ---------------- the riddim: a 2-bar bass line per track, restated on every chord ---------------- */
// Rhythms per bar, [step, length in steps]: the "and"s are swung like the hats. Degrees are steps of the track's mode from
// the chord's root (0 root, 2 its 3rd, 4 its 5th, 7 the octave, −3 the 5th below); strong beats (steps 0 and 8) take chord
// tones. The last bar before a chord change ends on an approach (the step above or below the next root, or its 5th below).
const RHYTHMS = {
  heart: [[0, 5], [and(6), 1.3], [8, 2.5], [12, 3], [and(14), 1.2]],
  walk: [[0, 3], [4, 2], [and(6), 1.3], [8, 3], [12, 2], [and(14), 1.2]],
  late: [[and(2), 1.4], [4, 3.5], [8, 2], [and(10), 1.3], [12, 3.5]],
  long: [[0, 7], [8, 3], [12, 3.5]],
  roll: [[0, 2], [and(2), 1.3], [4, 3.5], [8, 1.5], [and(10), 1.3], [12, 1.5], [and(14), 1.2]],
  hold: [[0, 11], [12, 2], [and(14), 1.2]],
  sync: [[0, 2.2], [and(2), 3.3], [and(6), 1.4], [8, 4], [and(14), 1.2]],
  drop: [[0, 6], [and(10), 1.3], [12, 2], [and(14), 1.2]],
};
const RIDDIMS = [
  { long: 1.2, hold: 1, heart: 1, drop: 0.8, late: 0.3 },
  { heart: 1, walk: 1, drop: 0.8, late: 0.7, sync: 0.6, long: 0.3 },
  { walk: 1, roll: 1, sync: 0.9, late: 0.7, heart: 0.6 },
];
const STRONG = { 0: 1, 4: 0.8, 7: 0.45, [-3]: 0.5, 2: 0.4 };
/** one move of the line: a step (p .45), a third (p .25), a leap to the 5th (down a 4th from the 3rd or higher, p .2), or
 *  the same note again */
function move(r, deg) {
  const u = r();
  if (u < 0.45) return deg + (r() < 0.5 ? 1 : -1);
  if (u < 0.7) return deg + (r() < 0.5 ? 2 : -2);
  if (u < 0.9) return deg >= 2 ? deg - 3 : 4;
  return deg;
}
function riddimOf(r, e) {
  const a = pickW(r, RIDDIMS[e]), b = r() < 0.55 ? a : pickW(r, RIDDIMS[e]);
  let deg = 0;
  const bar = id => RHYTHMS[id].map(([s, len], k) => {
    if (k === 0 && s === 0) deg = 0;
    else if (s % 8 === 0) deg = +pickW(r, STRONG);
    else deg = Math.max(-3, Math.min(8, move(r, deg)));
    return [s, deg, len, s % 8 === 0 ? 0.9 : s % 4 === 0 ? 0.8 : 0.72];
  });
  return [bar(a), bar(b)];
}
/** the track's mode bent to a chord: a chord tone the mode lacks replaces its neighbour a semitone away (not the tonic,
 *  not another chord tone), so a IV in Aeolian gets its major 3rd → 7 pitch classes over the tonic, ascending */
function scaleFor(plan, c) {
  const sc = MODES[plan.dub.mode].slice(), tones = QUALITIES[c.q].map(i => mod12(c.root + i));
  for (const pc of tones) {
    if (sc.includes(pc)) continue;
    const k = sc.findIndex(x => x !== 0 && !tones.includes(x) && (mod12(x - pc) === 1 || mod12(pc - x) === 1));
    if (k >= 0) sc[k] = pc;
  }
  return sc.sort((a, b) => a - b);
}
/** MIDI of `deg` steps of the chord's scale above its root `root` (a MIDI note) */
function degree(plan, c, root, deg) {
  const sc = scaleFor(plan, c), i = sc.indexOf(mod12(root - plan.key.tonic));
  if (i < 0) return root + [0, 2, 4, 5, 7, 9, 11][((deg % 7) + 7) % 7] + 12 * Math.floor(deg / 7);
  const n = i + deg, oct = Math.floor(n / 7);
  return root + sc[n - 7 * oct] + 12 * oct - sc[i];
}
/** the root of a chord at MIDI 33–44, nearest the last */
function rootNote(pc, prev = 38) {
  let best = 33 + mod12(pc - 33), bd = Infinity;
  for (let n = best; n <= 44; n += 12) { const d = Math.abs(n - prev); if (d < bd) { bd = d; best = n; } }
  return best;
}
function riddimLine({ plan, st, b, mode, chords, nextChords, chordAt, stop }) {
  const D = plan.dub, r = st.rS, end = stop ?? 16, pc = c => mod12(plan.key.tonic + c.root), out = [];
  if (mode === 'outro' && b.j === 2) {                             // the final tonic, held
    const m = rootNote(pc(chords[0]), st.prevBass);
    st.prevBass = m;
    return [{ s: 0, midi: m, vel: 0.84, dur: 28 }];
  }
  if (mode === 'kick' && b.j % 4 === 3 && r() < D.bassDrop) return [];       // the dub mix drops the bass out for a bar
  let notes = D.riddim[b.j % 2];
  if (mode === 'last') notes = D.riddim[1].filter(([s]) => s >= 8);         // the intro's last bar: a pickup into the A
  const lastC = chords[chords.length - 1], nx = nextChords && nextChords[0], change = nx && pc(nx) !== pc(lastC);
  notes = notes.filter(([s]) => s < end);
  notes.forEach(([s, deg, len, vel], k) => {
    const c = chordAt(s), root = rootNote(pc(c), st.prevBass), next = k + 1 < notes.length ? notes[k + 1][0] : end;
    let midi = degree(plan, c, root, deg), slide = false;
    const tones = QUALITIES[c.q].map(i => mod12(pc(c) + i));
    if (s % 8 === 0 && !tones.includes(mod12(midi))) midi = root;
    if (k === notes.length - 1 && change && s >= 12 && mode !== 'last') {  // approach the next root
      const tg = rootNote(pc(nx), root), u = r();
      midi = u < 0.45 ? degree(plan, nx, tg, 1) : u < 0.8 ? degree(plan, nx, tg, -1) : tg - 7;
      slide = r() < 0.25;
    } else slide = s % 4 !== 0 && r() < 0.12;
    while (midi < 31) midi += 12;
    while (midi > 55) midi -= 12;
    out.push({ s, midi, vel: vel * (mode === 'whole' ? 0.9 : 1), dur: Math.max(0.6, Math.min(len, next - s) * D.bass.art), slide });
    if (s % 8 === 0 || k === 0) st.prevBass = root;
  });
  return out;
}

/* ---------------- melodica tunes: simple, on the beat, long last notes ---------------- */
// Rhythm cells over 2 bars on quarter notes (the engine's B statement moves them by an 8th); the contour moves one step of
// the key's pentatonic (p .6), two (p .3) or stays, leaning downwards and turning around past ±3; the last note lasts 6–10
// steps.
const MCELLS = [[0, 4, 8, 16], [0, 8, 12, 16], [4, 8, 12, 20], [0, 4, 12, 16, 24], [0, 8, 16, 20], [8, 12, 16, 28], [0, 4, 8, 12, 20]];
function dubMotif(r, key) {
  const steps = MCELLS[Math.floor(r() * MCELLS.length)], n = steps.length, last = 6 + Math.floor(r() * 5);
  const durs = steps.map((s, i) => (i < n - 1 ? Math.min(8, steps[i + 1] - s) : last)), contour = [0];
  let dir = r() < 0.6 ? -1 : 1;
  for (let i = 1; i < n; i++) {
    const u = r(), mv = u < 0.6 ? dir : u < 0.9 ? 2 * dir : 0;
    contour.push(contour[i - 1] + mv);
    if (Math.abs(contour[i]) >= 3) dir = -Math.sign(contour[i]);
  }
  return { steps, durs, contour, vel: steps.map((s, i) => (i === 0 ? 0.78 : +(0.66 + 0.06 * r()).toFixed(3))), mode: key.mode };
}

/* ---------------- drums, comping ---------------- */
const U = [-3, -2, -1], LOW = [0];                               // picks: the skank's triad · the organ's left hand
const COMP = [
  { one: [0.8, 1.4], long: [0.6, 1.2], swell: [0.3, 0.8], bubble: [0, 0.3], lift: [0, 0.2], ands: [0, 0], throw: [0.1, 0.2] },
  { one: [0.6, 1.1], bubble: [0.4, 0.9], ands: [0.2, 0.6], lift: [0.2, 0.5], long: [0.1, 0.3], swell: [0, 0.1], throw: [0.14, 0.26] },
  { ands: [0.6, 1.1], bubble: [0.5, 1], one: [0.3, 0.6], lift: [0.3, 0.6], long: [0, 0.1], swell: [0, 0], throw: [0.14, 0.26] },
];
const SKANK = [{ organ: 1, both: 0.6, guitar: 0.5 }, { both: 1, guitar: 0.8, organ: 0.6 }, { guitar: 1, both: 1, organ: 0.4 }];

export default {
  id: 'dub', name: 'Dub', desc: 'One-drop beat, offbeat skank and deep bass through a tape echo',
  //            chill                                   balanced                                  upbeat
  vary: 'colour',
  energy: {
    stretch:    [0.55,                                  0.45,                                     0.35],
    bpm:        [[64, 70],                              [68, 76],                                 [74, 84]],
    swing:      [[56, 62],                              [54, 60],                                 [50, 54]],   // + AND: 8ths swung 63–67 % · 62–66 % · 60–63 %
    snareLag:   [[0.006, 0.014],                        [0.004, 0.011],                           [0.002, 0.008]],
    groove:     [[{ drop: 1.4, sparse: 1, roots: 0.5 }, { drop: 1.4, sparse: 0.8, roots: 0.6 }],
                 [{ drop: 1.3, roots: 0.8, lift: 0.7, rockers: 0.5, sparse: 0.3 }, { drop: 1, roots: 0.8, lift: 0.7, rockers: 0.9 }],
                 [{ rockers: 1, steppers: 0.8, drop: 0.7, lift: 0.5 }, { rockers: 1, steppers: 0.9, drop: 0.6, lift: 0.5 }]],
    drumLevel:  [0.8,                                   0.88,                                     0.95],
    kitLp:      [[4200, 6000],                          [5000, 7200],                             [5800, 8200]],
    epLp:       [[2000, 2700],                          [2300, 3100],                             [2600, 3500]],     // the guitar chop's LP
    strum:      [[0.012, 0.02],                         [0.01, 0.016],                            [0.008, 0.014]],
    epVel:      [[0.5, 0.6],                            [0.54, 0.64],                             [0.56, 0.68]],
    comp:       COMP,
    push:       [0,                                     0,                                        0],
    legato:     [0.8,                                   0.7,                                      0.55],
    lead:       [0.55,                                  0.65,                                     0.75],
    softLead:   [0.6,                                   0.5,                                      0.4],              // the darker melodica
    tone:       [[4200, 6200],                          [5000, 7500],                             [6000, 8500]],
    wowCents:   [[6, 11],                               [5, 9],                                   [4, 8]],
    dust:       [[1, 1.6],                              [0.8, 1.4],                               [0.7, 1.2]],
    introHats:  [0.35,                                  0.5,                                      0.65],
    leadFrom:   [2,                                     2,                                        1],
    hatsFirst:  [0.35,                                  0.25,                                     0.15],
    breakDrums: [0.4,                                   0.6,                                      0.8],
    breakLead:  [0.3,                                   0.4,                                      0.5],
    // development (arrange.js BY_ENERGY): no lift (the riddim); the riddim first
    lift:       [0, 0, 0],
    solo:       [0.1, 0.15, 0.2],
    intro:      [{ keys: 0.2, beat: 0.35, groove: 0.25, lead: 0.1, cold: 0.1 }, { keys: 0.2, beat: 0.35, groove: 0.25, lead: 0.1, cold: 0.1 }, { keys: 0.2, beat: 0.35, groove: 0.25, lead: 0.1, cold: 0.1 }],
  },
  qualities: { dubmin: [0, 3, 7], dubmaj: [0, 4, 7] },
  progressions: {
    minor: {                                                                                     // two-chord vamps, few extensions
      d1: [[0, 'dubmin', 4], [10, 'dubmaj', 4], [0, 'dubmin', 4], [10, 'dubmaj', 4]],             // i ♭VII i ♭VII
      d2: [[0, 'm7', 8], [5, 'm7', 8]],                                                           // i7 iv7
      d3: [[0, 'dubmin', 4], [8, 'dubmaj', 4], [0, 'dubmin', 4], [8, 'dubmaj', 4]],               // i ♭VI i ♭VI
      d4: [[0, 'dubmin', 4], [5, 'dubmin', 4], [0, 'dubmin', 4], [5, 'dubmin', 4]],               // i iv i iv
      d5: [[0, 'dubmin', 4], [10, 'dubmaj', 4], [8, 'dubmaj', 4], [10, 'dubmaj', 4]],             // i ♭VII ♭VI ♭VII
      d6: [[0, 'm7', 12], [10, 'dubmaj', 4]],                                                     // i7 (3 bars) ♭VII: one-chord riddim
      d7: [[0, 'm7', 4], [5, 'dubmaj', 4], [0, 'm7', 4], [5, 'dubmaj', 4]],                       // i7 IV (Dorian)
    },
    major: {
      D1: [[0, 'dubmaj', 4], [5, 'dubmaj', 4], [0, 'dubmaj', 4], [5, 'dubmaj', 4]],               // I IV I IV
      D2: [[0, 'dubmaj', 4], [5, 'dubmaj', 4], [7, 'dubmaj', 4], [5, 'dubmaj', 4]],               // I IV V IV
      D3: [[0, 'dubmaj', 4], [10, 'dubmaj', 4], [5, 'dubmaj', 8]],                                // I ♭VII IV (Mixolydian)
      D4: [[9, 'm7', 8], [5, 'maj7', 8]],                                                         // vi7 IVmaj7 (lovers rock)
      D5: [[0, 'dubmaj', 8], [5, 'dubmaj', 8]],                                                   // I IV, two bars each
      D6: [[0, 'dubmaj', 4], [5, 'dubmaj', 4], [10, 'dubmaj', 4], [5, 'dubmaj', 4]],              // I IV ♭VII IV
    },
  },
  turnarounds: { major: [[5, 'dubmaj', 2], [7, 'dubmaj', 2]], minor: [[8, 'dubmaj', 2], [10, 'dubmaj', 2]] },   // IV|V · ♭VI|♭VII
  grids: {
    //          0123456789abcdef
    drop:     { K: '........x.......', R: '........x.......', H: 'x.x.x.x.x.x.x.x.' },                        // one drop: kick + cross-stick on 3
    roots:    { K: '........x.......', S: '........x.......', H: 'x.x.x.o.x.x.x.x.' },                        // the snare on 3, an open hat on the and of 2
    lift:     { K: '........x.......', R: '........x.....s.', H: 'x.x.x.x.x.x.x.x.' },                        // a ghost cross-stick on the and of 4
    rockers:  { K: 'x.......x.......', R: '........x.......', H: 'x.x.x.x.x.x.x.x.', X: '..s...s...s...s.' },  // kick on 1 and 3, shaker on the ands
    steppers: { K: 'x...x...x...x...', S: '........x.......', H: 'x.x.x.x.x.x.x.x.', R: '....s.......s...' },  // four on the floor
    sparse:   { K: '........x.......', R: '........x.......', H: 'x...x...x...x...' },                        // chill: quarter hats
    break:    { R: '........x.......', H: 'x...x...x...x...' },                                               // no kick: rim, hats and the echo
  },
  fills: {
    tom: { roll: [[12, 0.55], [13, 0.48], [14, 0.66]], voice: 'tom' },        // a triplet-ish tom pickup (13 and 14 swing)
    snare: { roll: [[12, 0.5], [13, 0.42], [14, 0.62]] },
    stop: { stop: 12 },                                                         // the band drops out for a beat: the echo rings
    open: { open: 14 },
  },
  bVariation: false,
  // [step, length, vel, pick]: chops on 2 and 4 (one), on every "and" (ands), the organ's left hand bubbling between them,
  // longer chops and a held organ breath for chill; `throw` asks for velocity 2.5, which the engine clamps to exactly 1: the
  // skank reads that as "throw this chop into the echo"
  comps: {
    one:    [[4, 1, 1, U], [12, 1, 0.95, U]],
    ands:   [[and(2), 0.8, 0.9, U], [and(6), 0.8, 0.82, U], [and(10), 0.8, 0.9, U], [and(14), 0.8, 0.82, U]],
    bubble: [[and(2), 0.8, 0.62, LOW], [4, 1, 1, U], [and(6), 0.8, 0.55, LOW], [and(10), 0.8, 0.62, LOW], [12, 1, 0.95, U], [and(14), 0.8, 0.55, LOW]],
    lift:   [[4, 1, 1, U], [and(10), 0.7, 0.62, U], [12, 1, 0.95, U]],
    long:   [[4, 3.5, 0.72, U], [12, 3.5, 0.7, U]],
    swell:  [[0, 7.5, 0.5], [12, 1, 0.92, U]],
    throw:  [[4, 1, 1, U], [12, 1.4, 2.5, U]],
  },
  keysLevel: 1.8,

  plan(plan, { r, energy }) {
    const e = ENERGY[energy] ?? 1, rr = (a, b, d = 3) => +(a + r() * (b - a)).toFixed(d);
    const loops = Object.fromEntries(Object.entries(plan.ep.comp).filter(([id]) => id !== 'throw')), loop = pickW(r, loops);   // one cell carries the riddim: ×3
    plan.ep.comp[loop] = +(plan.ep.comp[loop] * 3 + 0.3).toFixed(2);
    // the bass line's mode follows the chords: Dorian with a IV (the major 6th), else Aeolian; Mixolydian with a ♭VII
    const minor = plan.key.mode === 'minor', chords = trackProgs(plan).flatMap(p => p.chords);
    const pcs = new Set(chords.flatMap(c => QUALITIES[c.q].map(i => mod12(c.root + i))));
    const mode = minor ? (pcs.has(9) && !pcs.has(8) ? 'dorian' : 'aeolian') : (pcs.has(10) && !pcs.has(11) ? 'mixolydian' : 'ionian');
    // throws: the last bar of some 4-bar phrases in A and B, every other bar of the break
    const throws = [];
    let bar = 0;
    for (const s of plan.sections) {
      for (let j = 0; j < s.bars; j++, bar++) {
        const p = s.name === 'A' || s.name === 'B' ? (j % 4 === 3 ? [0.3, 0.35, 0.4][e] : 0) : s.name === 'break' ? (j % 2 ? 0.5 : 0) : 0;
        if (p && r() < p) throws.push(bar);
      }
    }
    const gPan = rr(0.18, 0.34, 2), side = r() < 0.5 ? 1 : -1;
    plan.dub = {
      mode, throws, riddim: riddimOf(r, e), bassDrop: [0.1, 0.14, 0.12][e],
      bass: { tri: rr(0.18, 0.32, 2), drive: rr(1.5, 2.2, 2), lp: rr(420, 620, 0), art: plan.bass.factor > 0.8 ? 0.94 : 0.72, level: [0.93, 0.97, 0.92][e] },
      desk: {
        steps: r() < [0.45, 0.6, 0.65][e] ? 3 : 4, fb: rr(0.42, 0.56, 2), fbThrow: rr(0.66, 0.72, 2), lp: rr(1500, 2400, 0), hp: rr(220, 360, 0),
        drive: rr(1.2, 2, 2), wobHz: rr(0.25, 0.6, 2), wobMs: rr(0.6, 1.4, 2), pan: +(-side * rr(0.2, 0.4)).toFixed(2), wet: rr(0.8, 1, 2),
        spring: { ms: rr(31, 41, 1), fb: rr(0.55, 0.68, 2), lp: rr(2400, 3200, 0), wet: rr(0.12, 0.2, 2) },
      },
      skank: {
        mode: pickW(r, SKANK[e]), vel: plan.ep.vel, drive: rr(1.4, 2, 2), gLp: plan.ep.lp, gDecay: rr(0.16, 0.26), pluck: rr(1.2, 1.6, 2), gPan: +(side * gPan).toFixed(2),
        oLp: rr(...[[1600, 2200], [1800, 2600], [2000, 2900]][e], 0), oPan: +(-side * gPan * 0.8).toFixed(2),
        chorus: { rate: rr(5.8, 6.8, 2), ms: rr(0.6, 1.1, 2) }, echo: { g: rr(0.35, 0.5, 2), o: rr(0.15, 0.25, 2) }, spring: rr(0.2, 0.35, 2),
        pad: { atk: rr(0.03, 0.08), rel: rr(0.14, 0.26) },
      },
      lead: { detune: rr(6, 12, 1), bp: rr(1100, 1600, 0), breath: rr(0.3, 0.5, 2), trem: rr(0.04, 0.08), echo: rr(0.22, 0.34, 2) },
    };
    // steppers has four kicks a bar and rockers two against the one drop's one: the busier grooves play a little softer
    plan.drums.level = +(plan.drums.level * ({ steppers: 0.86, rockers: 0.94 }[plan.drums.pattern] ?? 1)).toFixed(3);
    const k = plan.drums.kit;
    plan.drums.kit = {
      kickF0: rr(92, 112, 1), kickF1: rr(47, 54, 1), kickDecay: rr(...[[0.19, 0.25], [0.17, 0.23], [0.14, 0.19]][e]), snareF: rr(2200, 3000, 0), snareDecay: rr(0.07, 0.1),
      rimF: rr(480, 580, 0), hatHP: rr(6500, 8500, 0), hatClosed: rr(0.015, 0.024), openDecay: rr(0.16, 0.24), tomF0: rr(190, 230, 0),
      tomF1: rr(140, 165, 0), lp: k.lp, echo: { rim: rr(0.2, 0.32, 2), snare: rr(0.18, 0.3, 2), tom: 0.3 }, spring: rr(0.3, 0.5, 2),
    };
    if (plan.lead) {
      plan.lead.timbre = plan.lead.timbre === 'soft' ? 'soft' : 'reed';
      plan.lead.pan = +(-side * (0.1 + r() * 0.15)).toFixed(2);                     // across from the guitar
      plan.lead.motif = dubMotif(r, plan.key);
    }
  },

  voicing: skankVoicing,
  bass: riddimLine,
  voices(ac, dry, wet, plan, ctx) {
    const lite = ctx.cap < 16, o = { registry: ctx.registry, tag: ctx.tag, t0: ctx.t0, stepDur: ctx.stepDur, lite };
    const sub = () => rng(Math.floor(ctx.r() * 4294967296)), desk = createDesk(ac, dry, wet, plan.dub.desk, o);
    return {
      ep: createSkank(ac, dry, wet, desk, plan.dub.skank, { ...o, cap: ctx.cap, r: sub(), log: ctx.log }),
      bass: createDubBass(ac, dry, plan.dub.bass, o),
      kit: createDubKit(ac, dry, wet, desk, plan, o),
      lead: plan.lead ? createMelodica(ac, dry, wet, desk, plan.lead, plan.dub.lead, o) : null,
    };
  },
};
